import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientMutation, clientQuery } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager";
import { resetALL } from "Redux/actions";
import { clientGET } from 'Core/data/RestApi';

const INIT_STATE = {
  open: false,
  loading: false,
  result: null,
  error: null,
  items: [],
  csvFile: null,
  urlFileCsv: null,
  file: null,
  imported: false,
  importResult: [],
  dataCsv: []
};

const route = 'modules/maintenance/views/main/components/emissionCheckTab/modals/import/redux/slice';

export const importEmissionCheck = createAsyncThunk(
  "maintenance/carb/components/importCarb",
  async (payload, { rejectWithValue }) => {
    const { fileKey, delimiter } = payload;

    try {
      let data = await clientMutation(
        `
          mutation importCarbSchedules ($fileKey: String!, $delimiter: String!) {
            data: importCarbSchedules (fileKey: $fileKey, delimiter: $delimiter) {
              items {
                vin
                result
                error
              }
            }
          }
      `,
        {
          delimiter,
          fileKey
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (data?.data) {
        return data.data;
      } else {
        throw "Error while importing carb";
      }
    } catch (exc) {
      ExceptionManager(exc, route, "importCarbAction");
      rejectWithValue(exc);
    }
  }
);

export const loadFileCsv = createAsyncThunk(
  "maintenance/carb/components/loadFileCsv",
  async (payload, { rejectWithValue }) => {
    const { Url } = payload;

    try {
      const result = await clientGET('', {}, Url, false);
      if (result) {
        return { error: false, file: result };
      } else {
        throw "Error while loading CSV file";
      }
    } catch (exc) {
      ExceptionManager(
        exc,
        "modules/maintenance/views/carb/redux/sagas",
        "loadFileCsv",
        true
      );
      return rejectWithValue({ error: true, file: null });
    }
  }
);

export const getUrlFileAttachment = createAsyncThunk(
  "maintenance/carb/components/getUrlFileAttachment",
  async (payload, { rejectWithValue }) => {
    const { id } = payload;

    try {
      const data = await clientQuery(
        `query (
          $id: Int!
        ){
          downloadAttachmentUrl: downloadAttachmentUrl(
            id: $id
          )
        }`,
        {
          id
        },
        endpoints.GRAPHQL_GENERAL,
      );

      if (data && data.downloadAttachmentUrl && data.downloadAttachmentUrl) {
        return data.downloadAttachmentUrl;
      } else {
        throw 'There are not url attachment';
      }
    } catch (exc) {
      ExceptionManager(
        exc,
        'modules/maintenance/views/carb/redux/sagas',
        'getUrlFileAttachment',
      );
      return rejectWithValue(null);
    }
  }
);

export const downloadCsvData = createAsyncThunk(
  "maintenance/carb/components/downloadCsvData",
  async (payload, { rejectWithValue }) => {

    try {
      let data = await clientQuery(
        `
          query downloadCsvData {
            data: downloadCsvData {
              items {
                vin
                registrationState
                registrationCountry
                registrationExpiration
                enrolled
              }
            }
          }
      `,
        {},
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (data?.data) {
        return data.data?.items || [];
      } else {
        throw "Error while download carb";
      }
    } catch (exc) {
      ExceptionManager(exc, route, "downloadCsvData");
      rejectWithValue(exc);
    }
  }
);

const emissionCheckImportRedux = createSlice({
  name: "emissionCheck/import",
  initialState: INIT_STATE,
  reducers: {
    reset(state, action) {
      return action.payload ? { ...state, ...action.payload } : { ...INIT_STATE };
    },
    openImportEmissionCheck(state, action) {
      state.open = true;
      state.imported = false;
      state.importResult = [];
      state.items = action?.payload?.items || [];
    },
  },
  extraReducers: (builder) => {
    //use for reset all when logout
    builder.addCase(resetALL, () => INIT_STATE);

    //import
    builder.addCase(importEmissionCheck.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(importEmissionCheck.fulfilled, (state, action) => {
      state.loading = false;
      state.imported = true;
      state.importResult = action.payload;
    });
    builder.addCase(importEmissionCheck.rejected, (state, action) => {
      state.loading = false;
      state.imported = true;
      state.importResult = action.payload;
    });

    //loadFileCsv
    builder.addCase(loadFileCsv.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(loadFileCsv.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.csvFile = payload.file;
      state.csvFileError = payload.error;
    });
    builder.addCase(loadFileCsv.rejected, (state, { payload }) => {
      state.loading = false;
      state.csvFile = payload?.file || null;
      state.csvFileError = payload?.error || true;
    });

    //getUrlFileAttachment
    builder.addCase(getUrlFileAttachment.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getUrlFileAttachment.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.urlFileCsv = payload;
    });
    builder.addCase(getUrlFileAttachment.rejected, (state, { payload }) => {
      state.loading = false;
      state.urlFileCsv = payload;
    });

    //downloadCsvData
    builder.addCase(downloadCsvData.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(downloadCsvData.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.dataCsv = payload;
    });
    builder.addCase(downloadCsvData.rejected, (state, { payload }) => {
      state.loading = false;
      state.dataCsv = [];
    });
  },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = emissionCheckImportRedux;

// Extract and export each action creator by name
export const { reset, openImportEmissionCheck } = actions;

// Export the reducer, either as a default or named export
export default reducer;